import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import styled from 'styled-components';

import { type RegistrationCredential, webauthnBeginRegistration, webauthnFinishRegistration } from '@column/api';
import {
  Button,
  Icon,
  Input,
  Modal,
  ModalSize,
  Table,
  TableColumn,
  TableVariant,
  Tooltip,
} from '@column/column-ui-kit';
import { Timestamp } from '@column/ui';

import { createPublicKeyCredential, getDefaultPasskeyName, isWebAuthnSupported } from '~/lib/webauthn';
import { Factor, FactorTypeResponse } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { formatDateTime, getFormattedDate } from '~/util';

const PasskeyAddedTimestamp: React.FC<{ date: Date }> = ({ date }) => {
  const [relativeTime] = useState(() => getFormattedDate(date, 'time-passed'));

  return (
    <Timestamp
      date={date}
      className="text-xs text-muted-foreground"
      formatHover={(hoverDate) => formatDateTime(hoverDate, { timeZoneFormat: 'local' })}
    >
      {relativeTime}
    </Timestamp>
  );
};

const columns: TableColumn<Factor>[] = [
  {
    header: 'Name',
    accessor: 'displayName',
    width: { min: 240, max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={value} weight="medium" />,
  },
  {
    header: 'Added',
    accessor: 'createdAt',
    width: { min: 180, max: 220 },
    cell: ({ value }) => <Table.Cell>{value ? <PasskeyAddedTimestamp date={value} /> : '-'}</Table.Cell>,
  },
];

const HeaderAction = styled.div`
  grid-column: -2 / -1;
  justify-self: end;
`;

const NamePasskeyFields = styled.div`
  display: grid;
  grid-gap: 16px;
  padding-bottom: var(--page-margin);
`;

const NamePasskeyActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
`;

interface PendingRegistration {
  challengeId: string;
  credential: RegistrationCredential;
}

export const AddPasskeyButton: React.FC = () => {
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();
  const firstName = useSessionStore((state) => state.currentUser?.firstName ?? '');
  const refetchCurrentUser = useSessionStore((state) => state.refetchCurrentUser);
  const isSupported = isWebAuthnSupported();
  const [isOpen, setIsOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [pendingRegistration, setPendingRegistration] = useState<PendingRegistration>();

  const beginPasskeyRegistrationMutation = useMutation({
    mutationFn: async (): Promise<PendingRegistration> => {
      const { data: registration } = await webauthnBeginRegistration({ throwOnError: true });
      const credential = await createPublicKeyCredential(registration.options);

      return {
        challengeId: registration.challenge_id,
        credential,
      };
    },
    onSuccess: (registration) => {
      setPendingRegistration(registration);
      setDisplayName(getDefaultPasskeyName(firstName));
      setIsOpen(true);
    },
    onError: (error: unknown) => {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        return;
      }
      const content = error instanceof Error ? error.message : 'Failed to add passkey. Please try again.';
      addDangerNotification({ content });
    },
  });

  const finishPasskeyRegistrationMutation = useMutation({
    mutationFn: async ({ name, registration }: { name: string; registration: PendingRegistration }) => {
      await webauthnFinishRegistration({
        body: {
          challenge_id: registration.challengeId,
          credential: registration.credential,
          display_name: name,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      addSuccessNotification({ content: 'Passkey added' });
      void refetchCurrentUser();
      setIsOpen(false);
      setDisplayName('');
      setPendingRegistration(undefined);
    },
    onError: (error: unknown) => {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        return;
      }
      const content = error instanceof Error ? error.message : 'Failed to add passkey. Please try again.';
      addDangerNotification({ content });
    },
  });

  const addButton = (
    <Button
      type="button"
      size="small"
      variant="secondary"
      icon={<Icon.Plus />}
      isDisabled={!isSupported || beginPasskeyRegistrationMutation.isPending}
      isLoading={beginPasskeyRegistrationMutation.isPending}
      onClick={() => beginPasskeyRegistrationMutation.mutate()}
    >
      Add Passkey
    </Button>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setDisplayName('');
          setPendingRegistration(undefined);
        }}
        size={ModalSize.Small}
      >
        <Modal.IconCircle type="primary">
          <Icon.Key />
        </Modal.IconCircle>

        <form
          onSubmit={(event) => {
            event.stopPropagation();
            event.preventDefault();
            const name = displayName.trim();
            if (name && pendingRegistration) {
              finishPasskeyRegistrationMutation.mutate({ name, registration: pendingRegistration });
            }
          }}
        >
          <Modal.Content>
            <Modal.Headline fullWidth align="center">
              Name your passkey
            </Modal.Headline>
            <NamePasskeyFields>
              <Modal.Paragraph fullWidth align="center">
                Use a name that helps you recognize this key
              </Modal.Paragraph>
              <Input value={displayName} onChange={setDisplayName} placeholder="e.g. Work laptop" maxLength={127} />

              <NamePasskeyActions>
                <Button
                  type="button"
                  size="small"
                  variant="secondary"
                  onClick={() => {
                    setIsOpen(false);
                    setDisplayName('');
                    setPendingRegistration(undefined);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="small"
                  isDisabled={!displayName.trim() || !pendingRegistration}
                  isLoading={finishPasskeyRegistrationMutation.isPending}
                >
                  Add passkey
                </Button>
              </NamePasskeyActions>
            </NamePasskeyFields>
          </Modal.Content>
        </form>
      </Modal>

      <HeaderAction>
        {isSupported ? addButton : <Tooltip content="Passkeys are not supported by this browser">{addButton}</Tooltip>}
      </HeaderAction>
    </>
  );
};

export const SectionPasskeys: React.FC = () => {
  const currentUser = useSessionStore((state) => state.currentUser);
  const passkeys = currentUser?.factors.filter((factor) => factor.factorType === FactorTypeResponse.WEBAUTHN) ?? [];

  return (
    <Table
      columns={columns}
      data={passkeys}
      variant={TableVariant.List}
      getRowId={(passkey) => passkey.factorId}
      emptyMessage="No passkeys configured"
    />
  );
};
